import { useAppRouteParams } from "@/AppRoutes";
import { useAppAccount } from "@/components/AppProvider/AppAccountProvider";
import {
  GridTableTemplate,
  renderGridTableDateTimeCell,
} from "@/components/GridTable/helpers";
import gridStyles from "@/components/GridTable/index.module.css";
import { MessagesTooltip } from "@/components/MessagesTooltip";
import {
  FrontendAddonVersionRaw,
  FrontendAddonWithTargetVersionRaw,
} from "@/domain/FrontendAddon";
import { Button, MenuItem, MenuList } from "@fluentui/react-components";
import {
  ArrowClockwise20Regular,
  Delete20Regular,
} from "@fluentui/react-icons";
import React, { useCallback, useMemo } from "react";
import { getFrontendAddonVersionName } from "../../../../helpers/frontendAddonVersionDisplay";
import styles from "../index.module.css";
import { FrontendAddonUsageOrg } from "../types";
import { UsageOrgsPopover } from "./UsageOrgsPopover";

// 削除できないのは「バージョンを固定して使用」で直接指定されている場合だけ。
// latest 指定の組織は削除後に自動で次の最新へ切り替わるため対象外とする
const checkIsInUseVersion = (
  version: FrontendAddonVersionRaw,
  targetFrontendAddon: FrontendAddonWithTargetVersionRaw | undefined
): boolean => {
  if (!targetFrontendAddon) return false;
  return targetFrontendAddon.target_version === version.id;
};

const renderVersionNameCell = (
  version: FrontendAddonVersionRaw,
  isLatest: boolean
) => (
  <div className={styles.versionNameCell}>
    {isLatest ? <div className={styles.latestBadge}>latest</div> : null}
    <div className={styles.versionName}>
      {getFrontendAddonVersionName(version)}
    </div>
    <div className={styles.versionId}>ID : {version.id}</div>
  </div>
);

// 削除できる行は Tooltip で包まない (aria-describedby が常に付くのを避ける)
const renderDeleteMenuItem = (
  disabledReason: string | null,
  onSingleAction: (versionId: string) => void,
  versionId: string
) => {
  const menuItem = (
    <MenuItem
      onClick={() => {
        onSingleAction(versionId);
      }}
      disabled={Boolean(disabledReason)}
      className={disabledReason ? undefined : styles.dangerMenuItem}
    >
      削除
    </MenuItem>
  );
  if (!disabledReason) return menuItem;
  return (
    <MessagesTooltip messages={[disabledReason]} relationship="description">
      {menuItem}
    </MessagesTooltip>
  );
};

export const GridTableFrontendAddonVersions: React.FC<{
  targetFrontendAddon: FrontendAddonWithTargetVersionRaw | undefined;
  versions: FrontendAddonVersionRaw[];
  // latest 指定でこのアドオンを利用している組織 (admin のみ算出される)
  latestUsageOrgs: FrontendAddonUsageOrg[];
  selectedVersionIds: string[];
  setSelectedVersionIds: React.Dispatch<React.SetStateAction<string[]>>;
  hasDeletePermission: boolean;
  hasWritePermission: boolean;
  isAddonDisabled: boolean;
  isAddonNotEditable: boolean;
  onSingleAction: (versionId: string) => void;
  onBulkAction: () => void;
  onVersionUpdate: () => void;
}> = ({
  targetFrontendAddon,
  versions,
  latestUsageOrgs,
  selectedVersionIds,
  setSelectedVersionIds,
  hasDeletePermission,
  hasWritePermission,
  isAddonDisabled,
  isAddonNotEditable,
  onSingleAction,
  onBulkAction,
  onVersionUpdate,
}) => {
  const { isAADeveloperOrAbove } = useAppAccount();
  const { organizationId } = useAppRouteParams();

  // 表示中の組織が固定指定しているバージョンは「使用中」で示すため、他組織の利用から除く
  const countOtherOrgsUsage = useCallback(
    (version: FrontendAddonVersionRaw) =>
      (version.usage_orgs ?? []).filter((org) => org.org_id !== organizationId)
        .length,
    [organizationId]
  );

  const theadCells = useMemo(
    () =>
      isAADeveloperOrAbove
        ? ["バージョン名", "利用組織数", "作成日時", "更新日時"]
        : ["バージョン名", "作成日時", "更新日時"],
    [isAADeveloperOrAbove]
  );
  // GridTable ヘッダーの min-width 1000px に列幅合計を合わせる (checkbox 50 + その他 90)
  const cellsWidths = useMemo(
    () => (isAADeveloperOrAbove ? [300, 200, 180, 180] : [400, 230, 230]),
    [isAADeveloperOrAbove]
  );

  const getDeleteDisabledReason = useCallback(
    (version: FrontendAddonVersionRaw) => {
      if (!hasDeletePermission) return "削除の権限がありません";
      if (isAddonDisabled)
        return "無効化されたアドオンのバージョンは削除できません";
      if (isAddonNotEditable)
        return "編集権限がないアドオンのバージョンは削除できません";
      // api-server は最後の有効バージョンの削除を拒否する (versions は有効なもののみ)
      if (versions.length <= 1) return "最後のバージョンは削除できません";
      if (isAADeveloperOrAbove && countOtherOrgsUsage(version) > 0)
        return "他組織で利用中のバージョンは削除できません";
      if (checkIsInUseVersion(version, targetFrontendAddon))
        return "使用中のバージョンは削除できません";
      return null;
    },
    [
      targetFrontendAddon,
      isAADeveloperOrAbove,
      countOtherOrgsUsage,
      hasDeletePermission,
      isAddonDisabled,
      isAddonNotEditable,
      versions.length,
    ]
  );

  const tbodyRowsCells = useMemo(
    () =>
      versions.map((version) => {
        const isLatest = targetFrontendAddon?.latest_version === version.id;
        const deleteDisabledReason = getDeleteDisabledReason(version);
        return {
          id: version.id,
          rowCells: [
            renderVersionNameCell(version, isLatest),
            ...(isAADeveloperOrAbove
              ? [
                  <UsageOrgsPopover
                    key={`usage-${version.id}`}
                    directUsageOrgs={version.usage_orgs ?? []}
                    latestUsageOrgs={isLatest ? latestUsageOrgs : []}
                  />,
                ]
              : []),
            renderGridTableDateTimeCell(version.created_at),
            renderGridTableDateTimeCell(version.updated_at),
          ],
          contextMenuItems: (
            <MenuList>
              {renderDeleteMenuItem(
                deleteDisabledReason,
                onSingleAction,
                version.id
              )}
            </MenuList>
          ),
        };
      }),
    [
      versions,
      targetFrontendAddon,
      getDeleteDisabledReason,
      isAADeveloperOrAbove,
      latestUsageOrgs,
      onSingleAction,
    ]
  );

  const selectedVersions = useMemo(
    () => versions.filter((v) => selectedVersionIds.includes(v.id)),
    [versions, selectedVersionIds]
  );

  const hasInUseVersionSelected = useMemo(
    () =>
      selectedVersions.some((v) => checkIsInUseVersion(v, targetFrontendAddon)),
    [selectedVersions, targetFrontendAddon]
  );

  const hasUsedByOrgsVersionSelected = useMemo(
    () =>
      isAADeveloperOrAbove &&
      selectedVersions.some((v) => countOtherOrgsUsage(v) > 0),
    [isAADeveloperOrAbove, selectedVersions, countOtherOrgsUsage]
  );

  // 全件選択は最後の 1 件が必ず失敗するため事前に無効にする
  const willRemoveAllVersions =
    versions.length > 0 && selectedVersionIds.length >= versions.length;

  const isDisableBulkRemoveButton =
    !hasDeletePermission ||
    isAddonDisabled ||
    isAddonNotEditable ||
    selectedVersionIds.length === 0 ||
    willRemoveAllVersions ||
    hasInUseVersionSelected ||
    hasUsedByOrgsVersionSelected;

  const bulkDeleteDisabledReason = !hasDeletePermission
    ? "削除の権限がありません"
    : isAddonDisabled
      ? "無効化されたアドオンのバージョンは削除できません"
      : isAddonNotEditable
        ? "編集権限がないアドオンのバージョンは削除できません"
        : willRemoveAllVersions
          ? "すべてのバージョンは削除できません"
          : hasUsedByOrgsVersionSelected
            ? "他組織で利用中のバージョンが含まれています"
            : hasInUseVersionSelected
              ? "使用中のバージョンが含まれています"
              : null;

  const bulkRemoveButton = (
    <Button
      appearance="subtle"
      size="small"
      onClick={onBulkAction}
      disabled={isDisableBulkRemoveButton}
    >
      <Delete20Regular />
      削除
    </Button>
  );

  const headerElement = (
    <div className={gridStyles.header}>
      <div className={gridStyles.leftEndContent}>バージョン一覧</div>
      <div className={gridStyles.rightEndContent}>
        <div className={styles.tableHeaderActions}>
          <div className={styles.tableHeaderMenu}>
            {selectedVersionIds.length > 0 ? (
              <div className={styles.selectionCount}>
                {selectedVersionIds.length}件選択中
              </div>
            ) : null}
            {bulkDeleteDisabledReason ? (
              <MessagesTooltip
                messages={[bulkDeleteDisabledReason]}
                relationship="description"
              >
                {bulkRemoveButton}
              </MessagesTooltip>
            ) : (
              bulkRemoveButton
            )}
          </div>
          <div className={styles.tableHeaderDivider} />
          <Button
            appearance="primary"
            icon={<ArrowClockwise20Regular />}
            onClick={onVersionUpdate}
            disabled={
              !hasWritePermission || isAddonDisabled || isAddonNotEditable
            }
          >
            バージョン更新
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <GridTableTemplate
      headerElement={headerElement}
      theadCells={theadCells}
      tbodyRowsCells={tbodyRowsCells}
      cellsWidth={cellsWidths}
      checkedIds={selectedVersionIds}
      setCheckedIds={setSelectedVersionIds}
      enableContextMenuColumn
      fitContentHeight
      fixedColumnCounts={{ left: 2, right: 1 }}
    />
  );
};
